import { useEffect, useRef, useState, useCallback } from "react";
import { api, BACKEND_URL } from "@/lib/api";

/**
 * WebSocket client hook with auto-reconnect.
 * Requires backend to expose /api/ws/token which returns a short-lived JWT
 * (our access_token is httpOnly, so we fetch a copy here).
 */
export function useChatWebSocket(userId, onMessage) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const reconnectRef = useRef(null);

  const connect = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await api.get("/auth/ws-token");
      const wsUrl = BACKEND_URL.replace(/^http/, "ws");
      const url = `${wsUrl}/api/ws/chat?token=${encodeURIComponent(data.token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onmessage = (evt) => {
        try { onMessage && onMessage(JSON.parse(evt.data)); } catch (_) {}
      };
      ws.onclose = () => {
        setConnected(false);
        if (!reconnectRef.current) {
          reconnectRef.current = setTimeout(() => {
            reconnectRef.current = null;
            connect();
          }, 3000);
        }
      };
      ws.onerror = () => { try { ws.close(); } catch (_) {} };
    } catch (_) {
      /* not authenticated */
    }
  }, [userId, onMessage]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch (_) {} }
    };
  }, [connect]);

  const sendMessage = useCallback((toUserId, body) => {
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: "message", to: toUserId, body }));
      return true;
    }
    return false;
  }, []);

  return { connected, sendMessage };
}
